import { useState, useRef, useMemo, useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, RefreshCw, Settings, User, Clock, Play, CheckCircle, Cpu, Mic, FileText, Shield, Upload, Loader2, X, BookOpen } from "lucide-react";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { fetchCurrentCompanyId } from "@/hooks/useCurrentCompany";
import { toast } from "sonner";
import { useWorkflowDefaults } from "@/hooks/useWorkflowDefaults";
import { usePhaseDefaults } from "@/hooks/usePhaseDefaults";
import { useRefurbishmentEstimate, formatMinutes } from "@/hooks/useRefurbishmentEstimate";
import { useRequiredSpareParts, sumPartsPrice } from "@/hooks/useSparePartsCost";
import { StepsOverviewDialog } from "@/components/StepsOverviewDialog";
import { MatchingManualsPanel } from "@/components/MatchingManualsPanel";
import { useAppliance } from "@/hooks/useAppliance";
import { useApplianceMarketPrice } from "@/hooks/useApplianceMarketPrice";
import { useWorkflowRunner } from "@/workflows/runnerContext";

const CATEGORY_TO_APPLIANCE_TYPE: Record<string, string> = {
  "washing-machine": "Washing Machine",
  "washing machine": "Washing Machine",
  "dryer": "Dryer",
  "tumble dryer": "Dryer",
  "dishwasher": "Dishwasher",
  "oven": "Oven",
  "refrigerator": "Household Refrigerator",
  "household refrigerator": "Household Refrigerator",
  "vacuum-cleaner": "Vacuum Cleaner",
};

const normalizeApplianceType = (category: string | null | undefined): string => {
  if (!category) return "";
  return CATEGORY_TO_APPLIANCE_TYPE[category.toLowerCase()] || category;
};

const normalizeBrand = (brand: string | null | undefined): string => {
  const b = (brand || "").trim();
  if (!b) return b;
  const upper = b.toUpperCase();
  if (upper === "BSH") return "Bosch";
  if (upper === "AEG") return "AEG";
  if (upper === "LG") return "LG";
  return b.charAt(0).toUpperCase() + b.slice(1).toLowerCase();
};

const PHASE_CONFIG = [
  { key: "refurbish_start", defaultsKey: "refurbishStart" as const, label: "Refurbish Start" },
  { key: "error_codes", defaultsKey: "errorCodes" as const, label: "Common Error Codes" },
  { key: "troubleshooting", defaultsKey: "troubleshooting" as const, label: "Pre-repair Troubleshooting" },
  { key: "parts_replacement", defaultsKey: "partsReplacement" as const, label: "Parts Replacement Guides" },
  { key: "verification", defaultsKey: "verification" as const, label: "Final Verification" },
];

const RefurbishmentStart = () => {
  const { t } = useTranslation("refurb");
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const runner = useWorkflowRunner();
  const [manualUrl, setManualUrl] = useState<string | null>(null);
  const [manualFileName, setManualFileName] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [showStepsDialog, setShowStepsDialog] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [selectedManualId, setSelectedManualId] = useState<string | null>(null);
  const [selectedManualTitle, setSelectedManualTitle] = useState<string | null>(null);

  const { appliance: loadedAppliance, isLoading: isLoadingAppliance, notFound } = useAppliance(id);

  useEffect(() => {
    if (notFound) {
      toast.error(t("common.applianceNotFound"));
      navigate("/work-orders");
    }
  }, [notFound, navigate, t]);

  const appliance = loadedAppliance ?? {
    name: t("common.loading"),
    modelNumber: "",
    category: t("process.phaseNames.initialAssessment"),
    brand: "",
  };

  const { defaults, isLoading: isLoadingWorkflow } = useWorkflowDefaults(
    appliance.brand, appliance.category, "refurbish", appliance.modelNumber
  );

  const { isPhaseEnabled } = usePhaseDefaults(appliance.category, appliance.brand);

  const { totalMinutes, isLoading: isLoadingEstimate } = useRefurbishmentEstimate(
    appliance.brand,
    appliance.category,
    [],
    appliance.modelNumber,
  );

  const { data: requiredParts = [], isLoading: isLoadingParts } = useRequiredSpareParts(
    normalizeApplianceType(appliance.category),
    normalizeBrand(appliance.brand),
    appliance.modelNumber,
  );

  // Fetch (or look up cached) market price the moment the pre-triage starts.
  // Skips scrape entirely if a price already exists in new_appliance_prices.
  const { price: marketPrice, isLoading: isLoadingMarketPrice } = useApplianceMarketPrice({
    brand: normalizeBrand(appliance.brand),
    model: appliance.modelNumber,
    category: appliance.category,
    enabled: !!appliance.brand && !!appliance.modelNumber,
  });

  // Build phases from workflow defaults
  const processPhases = useMemo(() => {
    const phases: { number: number; title: string; stepCount: number; steps: string[]; items: { title: string; description: string | null }[] }[] = [];
    let phaseNum = 1;

    for (const phase of PHASE_CONFIG) {
      // Skip phase entirely if disabled for refurbish
      if (!isPhaseEnabled(phase.key, "refurbish")) continue;
      const items = defaults[phase.defaultsKey] || [];

      if (items.length > 0) {
        phases.push({
          number: phaseNum,
          title: phase.label,
          stepCount: items.length,
          steps: items.map(i => i.title),
          items: items.map(i => ({ title: i.title, description: i.description })),
        });
        phaseNum++;
      }
    }

    return phases;
  }, [defaults, isPhaseEnabled]);

  const totalSteps = processPhases.reduce((sum, p) => sum + p.stepCount, 0);
  const totalPhases = processPhases.length;

  // Fallback if no workflow data yet
  const fallbackPhases = [
    { number: 1, title: "Initial Assessment & Safety", stepCount: 2, steps: [] as string[], items: [] as { title: string; description: string | null }[] },
    { number: 2, title: "External Cleaning & Aesthetics", stepCount: 2, steps: [] as string[], items: [] as { title: string; description: string | null }[] },
    { number: 3, title: "Internal Cleaning & Component Check", stepCount: 2, steps: [] as string[], items: [] as { title: string; description: string | null }[] },
    { number: 4, title: "Testing & Final Preparation", stepCount: 2, steps: [] as string[], items: [] as { title: string; description: string | null }[] },
  ];

  const displayPhases = processPhases.length > 0 ? processPhases : fallbackPhases;
  const displayTotalSteps = processPhases.length > 0 ? totalSteps : 8;
  const displayTotalPhases = processPhases.length > 0 ? totalPhases : 4;

  const dialogSteps = useMemo(() => {
    let stepId = 0;
    return displayPhases.flatMap((phase) =>
      phase.items.length > 0
        ? phase.items.map((item) => ({
            id: stepId++,
            phase: phase.number,
            phaseName: phase.title,
            title: item.title,
            description: item.description || "",
            isSafetyCritical: false,
            estimatedTime: "10 min",
            tools: [],
          }))
        : [{
            id: stepId++,
            phase: phase.number,
            phaseName: phase.title,
            title: phase.title,
            description: "",
            isSafetyCritical: false,
            estimatedTime: "10 min",
            tools: [],
          }]
    );
  }, [displayPhases]);

  const handleManualUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.type !== "application/pdf") {
      toast.error(t("start.pdfRequired"));
      return;
    }
    if (file.size > 20 * 1024 * 1024) {
      toast.error(t("start.fileTooLarge"));
      return;
    }
    setIsUploading(true);
    try {
      if (!user) { toast.error(t("start.notAuthenticated")); return; }
      const safeName = file.name.replace(/[^\w.\-]/g, "_");
      const filePath = `${user.id}/${Date.now()}-${safeName}`;
      const { error: upErr } = await supabase.storage
        .from("repair-guides")
        .upload(filePath, file, { contentType: "application/pdf" });
      if (upErr) throw upErr;

      const companyId = await fetchCurrentCompanyId(user.id);
      if (!companyId) throw new Error(t("start.noCompany"));
      const { data: row, error: insErr } = await supabase
        .from("repair_manuals")
        .insert({
          user_id: user.id,
          company_id: companyId,
          file_path: filePath,
          file_size: file.size,
          original_filename: file.name,
          metadata_status: "pending",
        })
        .select("id")
        .single();
      if (insErr || !row) throw insErr || new Error("Insert failed");

      setManualUrl(filePath);
      setManualFileName(file.name);
      setSelectedManualId(row.id);
      setSelectedManualTitle(file.name);
      toast.success(t("start.uploadSuccess", { name: file.name }));

      // Background AI extraction
      supabase.functions.invoke("extract-manual-metadata", {
        body: { manualId: row.id, filePath },
      }).catch((e) => console.error("metadata extraction:", e));
    } catch (err: any) {
      toast.error(t("start.uploadFailed", { error: err.message || "Unknown error" }));
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const totalMaterialsCost = sumPartsPrice(requiredParts);
  const materialsCurrency = requiredParts[0]?.currency || "EUR";
  const formatPrice = (price: number, currency: string) => {
    try {
      return new Intl.NumberFormat(undefined, { style: "currency", currency }).format(price);
    } catch {
      return `${currency} ${price.toFixed(2)}`;
    }
  };

  const warrantyBenefits = [
    t("start.benefit1"),
    t("start.benefit2"),
    t("start.benefit3"),
    t("start.benefit4"),
    t("start.benefit5"),
    t("start.benefit6"),
  ];

  const professionalFeatures = [
    {
      icon: Cpu,
      title: t("start.featureAiTitle"),
      description: t("start.featureAiDesc"),
    },
    {
      icon: Mic,
      title: t("start.featureVoiceTitle"),
      description: t("start.featureVoiceDesc"),
    },
    {
      icon: FileText,
      title: t("start.featurePassportTitle"),
      description: t("start.featurePassportDesc"),
    },
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="flex items-center justify-between px-6 py-4 bg-card border-b border-border">
        <div className="flex items-center gap-6">
          <button
            onClick={() => navigate("/work-orders")}
            className="flex items-center gap-2 text-primary hover:text-primary/80 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="font-medium">{t("common.backToWorkOrders")}</span>
          </button>
          
          <div>
            <h1 className="font-bold text-foreground">FixID</h1>
            <div className="flex items-center gap-1 text-sm text-muted-foreground">
              <RefreshCw className="w-3 h-3" />
              <span>{t("start.subtitleNav")}</span>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-6">
          <div className="text-right">
            <p className="font-medium text-foreground">{appliance.name}</p>
            <div className="flex items-center gap-2 justify-end">
              <span className="text-sm text-muted-foreground">{appliance.modelNumber}</span>
              <span className="category-badge category-badge-washing text-xs">{appliance.category}</span>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <button className="p-2 rounded-lg hover:bg-muted transition-colors">
              <Settings className="w-5 h-5 text-muted-foreground" />
            </button>
            <button className="p-2 rounded-lg hover:bg-muted transition-colors">
              <User className="w-5 h-5 text-muted-foreground" />
            </button>
          </div>
        </div>
      </header>

      <main className="p-6 max-w-5xl mx-auto">
        {/* Title Card */}
        <div className="bg-card rounded-2xl border border-border p-6 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-refurbish/10 flex items-center justify-center">
                <RefreshCw className="w-6 h-6 text-refurbish" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-foreground">{t("start.title")}</h2>
                <p className="text-muted-foreground">{t("start.subtitle", { name: appliance.name })}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button variant="outline" className="gap-2">
                <Cpu className="w-4 h-4" />
                {t("start.aiAssistant")}
              </Button>
              <Button variant="outline" onClick={() => navigate(`/appliance/${id}`)}>
                {t("common.backToProduct")}
              </Button>
            </div>
          </div>
        </div>

        {/* Process Overview & Materials */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* Process Overview */}
          <div className="bg-card rounded-2xl border border-border p-6">
            <div className="flex items-center gap-2 mb-4">
              <Clock className="w-5 h-5 text-muted-foreground" />
              <h3 className="font-semibold text-foreground">{t("start.processOverview")}</h3>
              {isLoadingWorkflow && <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />}
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-4 mb-6">
              <div className="text-center p-4 bg-background rounded-xl border border-border">
                <p className="text-3xl font-bold text-primary">{displayTotalPhases}</p>
                <p className="text-sm text-muted-foreground">{t("start.phases")}</p>
              </div>
              <div className="text-center p-4 bg-background rounded-xl border border-border">
                <p className="text-3xl font-bold text-primary">{displayTotalSteps}</p>
                <p className="text-sm text-muted-foreground">{t("start.steps")}</p>
              </div>
              <div className="text-center p-4 bg-background rounded-xl border border-border">
                <p className="text-3xl font-bold text-primary">
                  {isLoadingEstimate ? <Loader2 className="w-6 h-6 animate-spin inline" /> : (totalMinutes > 0 ? formatMinutes(totalMinutes) : "—")}
                </p>
                <p className="text-sm text-muted-foreground">{t("start.estTime")}</p>
              </div>
            </div>

            {/* Phases List */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <p className="text-sm font-medium text-muted-foreground">{t("start.phasesList")}</p>
                <Button variant="outline" size="sm" className="text-xs gap-1.5" onClick={() => setShowStepsDialog(true)}>
                  <FileText className="w-3.5 h-3.5" />
                  {t("start.viewAllSteps")}
                </Button>
              </div>
              <div className="space-y-2">
                {displayPhases.map((phase) => (
                  <div key={phase.number} className="p-3 bg-background rounded-lg">
                    <div className="flex items-center gap-3">
                      <span className="w-6 h-6 rounded-full bg-primary text-primary-foreground text-sm font-medium flex items-center justify-center flex-shrink-0">
                        {phase.number}
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-foreground">{phase.title}</span>
                          <span className="text-xs text-muted-foreground ml-2">{t("start.stepsCount", { count: phase.stepCount })}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Required Materials */}
          <div className="bg-card rounded-2xl border border-border p-6">
            <div className="flex items-center gap-2 mb-4">
              <Settings className="w-5 h-5 text-muted-foreground" />
              <h3 className="font-semibold text-foreground">{t("start.requiredMaterials")}</h3>
              {isLoadingParts && <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />}
            </div>

            <div className="space-y-4 mb-6">
              {!isLoadingParts && requiredParts.length === 0 && (
                <p className="text-sm text-muted-foreground p-4 bg-background rounded-xl border border-border">
                  {t("start.noPartsConfigured")}
                </p>
              )}
              {requiredParts.map((part) => (
                <div key={part.id} className="flex items-center justify-between p-4 bg-background rounded-xl border border-border">
                  <div>
                    <p className="font-medium text-foreground">{part.name}</p>
                    {part.part_number && (
                      <p className="text-sm text-muted-foreground">{part.part_number}</p>
                    )}
                  </div>
                  <p className="text-lg font-semibold text-refurbish">
                    {formatPrice(Number(part.price || 0), part.currency || "EUR")}
                  </p>
                </div>
              ))}
            </div>

            <div className="flex items-center justify-between p-4 bg-muted rounded-xl mb-4">
              <span className="text-muted-foreground">{t("start.totalMaterialsCost")}</span>
              <span className="text-xl font-bold text-foreground">{formatPrice(totalMaterialsCost, materialsCurrency)}</span>
            </div>

            <div className="flex items-center justify-between p-4 bg-background border border-border rounded-xl mb-4">
              <div className="flex flex-col">
                <span className="text-sm text-muted-foreground">{t("start.currentMarketPrice")}</span>
                <span className="text-xs text-muted-foreground">
                  {marketPrice
                    ? `${marketPrice.source}${marketPrice.fromCache ? ` · ${t("start.cached")}` : ` · ${t("start.justFetched")}`}`
                    : isLoadingMarketPrice
                      ? t("start.lookingUpPrice")
                      : t("start.noPriceAvailable")}
                </span>
              </div>
              <span className="text-xl font-bold text-foreground">
                {marketPrice ? formatPrice(marketPrice.lowest, marketPrice.currency) : isLoadingMarketPrice ? <Loader2 className="w-5 h-5 animate-spin" /> : "—"}
              </span>
            </div>

            {/* Repair Manual: pick from library or upload new */}
            <div className="mb-4 space-y-3">
              <div className="flex items-center gap-2">
                <BookOpen className="w-4 h-4 text-muted-foreground" />
                <p className="text-sm font-medium text-foreground">{t("start.repairManual")}</p>
              </div>
              <MatchingManualsPanel
                brand={appliance.brand}
                modelNumber={appliance.modelNumber}
                
                activeManualId={selectedManualId}
                onSelectForAI={(mid, title) => {
                  setSelectedManualId(mid);
                  setSelectedManualTitle(title);
                  setManualUrl(null);
                  setManualFileName(null);
                  toast.success(t("start.manualSelectedToast", { title }));
                }}
              />
              <input ref={fileInputRef} type="file" accept="application/pdf" className="hidden" onChange={handleManualUpload} />
              {selectedManualId ? (
                <div className="flex items-center justify-between p-3 bg-refurbish/10 border border-refurbish/20 rounded-xl">
                  <div className="flex items-center gap-2 text-sm text-refurbish min-w-0">
                    <FileText className="w-4 h-4 shrink-0" />
                    <span className="truncate">{t("start.manualActive", { title: selectedManualTitle || manualFileName || t("start.libraryManual") })}</span>
                  </div>
                  <button
                    onClick={() => {
                      setSelectedManualId(null);
                      setSelectedManualTitle(null);
                      setManualUrl(null);
                      setManualFileName(null);
                    }}
                    className="p-1 hover:bg-muted rounded shrink-0"
                  >
                    <X className="w-4 h-4 text-muted-foreground" />
                  </button>
                </div>
              ) : (
                <Button
                  variant="outline"
                  className="w-full gap-2"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isUploading}
                >
                  {isUploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                  {isUploading ? t("start.uploading") : t("start.uploadNewPdf")}
                </Button>
              )}
            </div>

            <Button
              className="w-full bg-refurbish hover:bg-refurbish/90 text-refurbish-foreground gap-2"
              onClick={() => {
                if (runner) {
                  runner.complete({ manualUrl, manualId: selectedManualId, manualTitle: selectedManualTitle });
                  return;
                }
                const wo = new URLSearchParams(window.location.search).get("workOrderId");
                const qs = wo ? `?workOrderId=${wo}` : "";
                navigate(`/appliance/${id}/refurbish/process${qs}`, {
                  state: { manualUrl, manualId: selectedManualId, manualTitle: selectedManualTitle },
                });
              }}
            >
              <Play className="w-4 h-4" />
              {t("start.startProcess")}
            </Button>
          </div>
        </div>

        {/* Digital Passport Warranty */}
        <div className="bg-card rounded-2xl border border-border p-6 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <Shield className="w-5 h-5 text-muted-foreground" />
            <h3 className="font-semibold text-foreground">{t("start.warrantyTitle")}</h3>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div>
              <p className="font-medium text-foreground mb-3">{t("start.passportBenefits")}</p>
              <div className="space-y-2">
                {warrantyBenefits.map((benefit, index) => (
                  <div key={index} className="flex items-center gap-2 text-sm text-muted-foreground">
                    <CheckCircle className="w-4 h-4 text-refurbish flex-shrink-0" />
                    <span>{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="flex flex-col items-center justify-center p-6 bg-background rounded-xl">
              <p className="text-5xl font-bold text-primary mb-2">{t("start.yearsWarrantyValue")}</p>
              <p className="text-lg font-medium text-foreground">{t("start.yearsWarrantyLabel")}</p>
              <p className="text-sm text-muted-foreground text-center mt-1">
                {t("start.yearsWarrantyDesc")}
              </p>
            </div>
          </div>
        </div>

        {/* Professional Features */}
        <div className="bg-card rounded-2xl border border-border p-6">
          <h3 className="font-semibold text-foreground mb-4">{t("start.professionalFeatures")}</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {professionalFeatures.map((feature, index) => (
              <div key={index} className="text-center p-6 bg-background rounded-xl border border-border">
                <div className="w-12 h-12 mx-auto mb-3 rounded-xl bg-primary/10 flex items-center justify-center">
                  <feature.icon className="w-6 h-6 text-primary" />
                </div>
                <p className="font-medium text-foreground mb-1">{feature.title}</p>
                <p className="text-sm text-muted-foreground">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </main>

      <StepsOverviewDialog
        open={showStepsDialog}
        onOpenChange={setShowStepsDialog}
        steps={dialogSteps}
        currentStepIndex={-1}
        completedSteps={[]}
        onNavigateToStep={() => {}}
        readOnly
      />
    </div>
  );
};

export default RefurbishmentStart;
